import React, { useCallback, useMemo, useState } from "react";
import { useHistory, useParams } from "react-router-dom";
import { Flex, Link, Text, Button, QueryLoader, toast } from "@jsq/ds/core";
import { Sizes } from "@jsq/ds/styles";
import { useRootProfileContext } from "../../../contexts/RootProfileContext";
import {
  useGetDocumentDetailsQuery,
  DocumentType,
  DocumentUploadInviteDetails,
  useCancelUploadInviteMutation,
  useResendUploadInviteMutation,
} from "../../../graphql/supergraph.index";
import { DocumentUploadInviteStatus } from "../../../graphql/supergraphOperations";
import useDocumentProcessor from "../../../hooks/documents/useDocumentProcessor";
import { getErrorMessage } from "../../../utils/error_parser";
import { clientContext } from "../../../utils/graphql_utils";
import { DefaultModal } from "../../common/DefaultModal";
import { sharedHeaderStyle, textColorNeutral800 } from "../../common/style_vanilla.css";
import { provideLegalName } from "../../ProfileTree/utils";
import { DocumentCollector } from "../DocumentCollector";
import { DocumentData, DocumentInvitedUserInfo } from "../DocumentCollector/types";
import { InviteToUploadModal } from "../InviteToUploadModal";
import { formatDate } from "../utils";
import {
  buttonText,
  inviteCompleteWrapper,
  headerWrapper,
  inviteCompleteMessageText,
} from "./style_vanilla.css";
import { DocumentCollectorInvestorProps } from "./types";
import { mapDocumentsToFormProps } from "./utils";

export const DocumentCollectorInvestor: React.FC<DocumentCollectorInvestorProps> = ({
  caseId,
  basePath,
  onStepCompletion,
}) => {
  const history = useHistory();
  const { profileId } = useParams<{ profileId: string }>();
  const { rootProfileId } = useRootProfileContext();
  const isRoot = Number(profileId) === rootProfileId;
  const [showInviteModal, setShowInviteModal] = useState(false);
  const [invitedUser, setInvitedUser] = useState<DocumentInvitedUserInfo | null>(null);
  const [hasUnsavedChanges, setHasUnsavedChanges] = useState(false);
  const [showBackWarningModal, setShowBackWarningModal] = useState(false);
  const parsedProfileId = parseInt(profileId);

  const { processDocuments, loading } = useDocumentProcessor(caseId, parsedProfileId);

  const [cancelUploadInvite] = useCancelUploadInviteMutation({
    context: clientContext(),
  });
  const [resendUploadInvite] = useResendUploadInviteMutation({
    context: clientContext(),
  });
  const documentDetailsResult = useGetDocumentDetailsQuery({
    context: clientContext(),
    fetchPolicy: "no-cache",
    variables: {
      profileId: parsedProfileId,
      caseId: caseId,
    },
  });

  const complianceCase = useMemo(
    () =>
      documentDetailsResult.data?.complianceProfile?.complianceCases?.find(
        (complianceCase) => complianceCase.caseId === caseId,
      ),
    [documentDetailsResult.data?.complianceProfile?.complianceCases, caseId],
  );

  const profileName = useMemo(
    () => provideLegalName(complianceCase?.complianceCase?.investingProfile),
    [complianceCase?.complianceCase?.investingProfile],
  );

  const isInvestingProfile = useMemo(
    () => Boolean(complianceCase?.isInvestingProfile),
    [complianceCase?.isInvestingProfile],
  );

  const documentDetails = useMemo(
    () =>
      mapDocumentsToFormProps(
        documentDetailsResult.data?.complianceProfile?.rulesEngine?.documentTypes || [],
        (documentDetailsResult.data?.complianceProfile?.documents ||
          []) as unknown as DocumentType[],
      ),
    [
      documentDetailsResult.data?.complianceProfile?.rulesEngine?.documentTypes,
      documentDetailsResult.data?.complianceProfile?.documents,
    ],
  );

  const inviteDetails: DocumentUploadInviteDetails | null | undefined =
    documentDetailsResult.data?.complianceProfile?.inviteToUploadDocumentsDetails;

  const statusBannerInfo = useMemo(() => {
    if (inviteDetails?.status === DocumentUploadInviteStatus.UploadPending) {
      return {
        message: `${inviteDetails.emailAddress} was invited to complete on ${formatDate(inviteDetails?.emailInviteDateSent as string)}`,
        inviteId: inviteDetails?.id,
      };
    } else if (inviteDetails?.status === DocumentUploadInviteStatus.UploadCompleted) {
      return {
        message: `${inviteDetails.emailAddress} uploaded documents on ${formatDate(inviteDetails?.documentUploadDate as string)}`,
      };
    } else {
      return null;
    }
  }, [inviteDetails]);

  const enableInviteButton = useMemo(() => {
    if (inviteDetails?.status === DocumentUploadInviteStatus.UploadPending) {
      return false;
    } else {
      const hasMissingDocuments = documentDetails.some((doc) => doc.id === undefined);
      return !hasMissingDocuments;
    }
  }, [documentDetails, inviteDetails]);

  /**
   * Handle unsaved changes updates from DocumentCollector
   */
  const handleUnsavedChangesChange = useCallback((hasChanges: boolean) => {
    setHasUnsavedChanges(hasChanges);
  }, []);

  const goToSubscriptionHomePage = useCallback(() => {
    if (onStepCompletion) {
      onStepCompletion();
    } else {
      history.push(isRoot ? basePath : `${basePath}/overview/${profileId}`);
    }
  }, [history, basePath, onStepCompletion, isRoot, profileId]);

  /**
   * Handle navigation back with unsaved changes check
   */
  const handleNavigateBack = useCallback(() => {
    if (hasUnsavedChanges) {
      setShowBackWarningModal(true);
    } else {
      goToSubscriptionHomePage();
    }
  }, [hasUnsavedChanges, goToSubscriptionHomePage]);

  /**
   * Process documents and handle success/error feedback
   * @param formValues - The document form values to process
   */
  const handleDocumentProcessing = useCallback(
    (newFiles: DocumentData[], deleteDocIds: number[]) => {
      processDocuments(newFiles, deleteDocIds)
        .then(() => {
          toast.success("Documents updated successfully");
          setHasUnsavedChanges(false); // Clear unsaved changes after successful submission
          goToSubscriptionHomePage();
        })
        .catch((error) => {
          toast.error(getErrorMessage(error));
        });
    },
    [basePath, history, processDocuments, profileId],
  );

  return (
    <QueryLoader result={documentDetailsResult}>
      <Flex flexDirection="column">
        <Link
          to={isRoot ? basePath : `${basePath}/overview/${profileId}`}
          variant="muted"
          icon="arrow-left"
          iconPosition="left"
          align="left"
        >
          {profileName}
        </Link>
        <Flex className={headerWrapper}>
          <Text variant="h3" className={sharedHeaderStyle}>
            {`Documents for ${profileName}`}
          </Text>
          {!isInvestingProfile && (
            <Button
              variant="neutral"
              icon="file-upload"
              disabled={enableInviteButton}
              onClick={() => {
                setShowInviteModal(true);
              }}
            >
              Invite to complete
            </Button>
          )}
        </Flex>
        {statusBannerInfo && (
          <Flex className={inviteCompleteWrapper}>
            <Text variant="regular-md" className={inviteCompleteMessageText}>
              {statusBannerInfo?.message}
            </Text>
            {statusBannerInfo?.inviteId && (
              <Flex gap={Sizes.SM}>
                <Text
                  variant="strong-md"
                  className={buttonText}
                  onClick={() => {
                    void resendUploadInvite({
                      variables: {
                        inviteId: statusBannerInfo.inviteId,
                      },
                    }).then(() => {
                      void documentDetailsResult.refetch();
                    });
                  }}
                >
                  Resend
                </Text>
                <Text
                  variant="strong-md"
                  className={buttonText}
                  onClick={() => {
                    setInvitedUser({
                      id: statusBannerInfo.inviteId,
                      emailAddress: inviteDetails?.emailAddress || "",
                    });
                  }}
                >
                  Cancel
                </Text>
              </Flex>
            )}
          </Flex>
        )}
        <Text variant="regular-md" className={textColorNeutral800}>
          The following documents are required. You can skip this step for now if you dont have
          these documents available. You will need to return and upload any required documents
          before signing this subscription.
        </Text>
        <DocumentCollector
          isSubmitting={loading}
          documentDetails={documentDetails}
          onNavigateBack={handleNavigateBack}
          onComplete={handleDocumentProcessing}
          onUnsavedChangesChange={handleUnsavedChangesChange}
        />
      </Flex>
      <InviteToUploadModal
        show={showInviteModal}
        onClose={(inviteDetails) => {
          setShowInviteModal(false);
          if (inviteDetails) {
            void documentDetailsResult.refetch();
          }
        }}
        caseId={caseId}
        profileId={parsedProfileId}
      />
      <DefaultModal
        show={!!invitedUser}
        title="Warning"
        body={`Are you sure you want to cancel the invitation to complete sent to ${invitedUser?.emailAddress}? A new invitation can be sent once this invitation is canceled.`}
        negBtnText="Close"
        posBtnText="Cancel invitation"
        posBtnVariant="danger"
        onClose={() => setInvitedUser(null)}
        onConfirm={() => {
          if (!invitedUser) {
            return;
          }
          void cancelUploadInvite({
            variables: {
              inviteId: invitedUser.id,
            },
          }).then(() => {
            void documentDetailsResult.refetch();
            setInvitedUser(null);
          });
        }}
      />
      <DefaultModal
        show={showBackWarningModal}
        title="Warning"
        body="You have unsaved changes. Are you sure you want to go back without submitting?"
        negBtnText="Cancel"
        posBtnText="Back"
        posBtnVariant="danger"
        onClose={() => setShowBackWarningModal(false)}
        onConfirm={() => {
          setShowBackWarningModal(false);
          setHasUnsavedChanges(false);
          goToSubscriptionHomePage();
        }}
      />
    </QueryLoader>
  );
};
